import { Component, OnInit } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/database';
import { MatSnackBar } from '@angular/material';

const DB_PATH = '/meta/operationalCities/India';

interface City {
  _key: string;        // Firebase key (e.g. "75")
  name: string;
  value: string;
  cityId: number;
  available: boolean;
  availableForCustomer: boolean;
  radiusForSearch: number;
  gofloatersSpaceNameCounter: number;
  coords: { lat: number; long: number };
  _editing?: boolean;
  _saving?: boolean;
  // draft fields while editing
  _draft?: Partial<City>;
}

@Component({
  selector: 'app-config-cities',
  templateUrl: './config-cities.component.html',
  styleUrls: ['./config-cities.component.scss']
})
export class ConfigCitiesComponent implements OnInit {
  cities: City[] = [];
  filtered: City[] = [];
  loading = true;
  quickSearch = '';

  // Add-city panel
  showAddPanel = false;
  adding = false;
  newCity: Partial<City> = this.emptyCity();

  constructor(private db: AngularFireDatabase, private snack: MatSnackBar) {}

  ngOnInit() {
    this.db.database.ref(DB_PATH).once('value', snapshot => {
      const val = snapshot.val() || {};
      this.cities = Object.keys(val).map(key => this.normalize(key, val[key]));
      this.cities.sort((a, b) => a.name.localeCompare(b.name));
      this.loading = false;
      this.applyFilter();
    });
  }

  private normalize(key: string, raw: any): City {
    return {
      _key: key,
      name: raw.name || '',
      value: raw.value || '',
      cityId: raw.cityId || 0,
      available: raw.available === true,
      availableForCustomer: raw.availableForCustomer === true,
      radiusForSearch: raw.radiusForSearch || 0,
      gofloatersSpaceNameCounter: raw.gofloatersSpaceNameCounter || 0,
      coords: { lat: (raw.coords || {}).lat || 0, long: (raw.coords || {}).long || 0 },
    };
  }

  applyFilter() {
    const q = this.quickSearch.trim().toLowerCase();
    this.filtered = q
      ? this.cities.filter(c => c.name.toLowerCase().includes(q) || c.value.toLowerCase().includes(q))
      : [...this.cities];
  }

  // ── Edit in place ─────────────────────────────────────────────────────────
  startEdit(city: City) {
    city._draft = {
      name: city.name,
      value: city.value,
      cityId: city.cityId,
      available: city.available,
      availableForCustomer: city.availableForCustomer,
      radiusForSearch: city.radiusForSearch,
      gofloatersSpaceNameCounter: city.gofloatersSpaceNameCounter,
      coords: { lat: city.coords.lat, long: city.coords.long },
    };
    city._editing = true;
  }

  cancelEdit(city: City) {
    city._editing = false;
    city._draft = undefined;
  }

  async saveEdit(city: City) {
    const d = city._draft;
    city._saving = true;
    const payload = {
      name: d.name,
      value: d.value,
      cityId: Number(d.cityId),
      available: d.available,
      availableForCustomer: d.availableForCustomer,
      radiusForSearch: Number(d.radiusForSearch),
      gofloatersSpaceNameCounter: Number(d.gofloatersSpaceNameCounter),
      coords: { lat: Number(d.coords.lat), long: Number(d.coords.long) },
    };
    try {
      await this.db.database.ref(`${DB_PATH}/${city._key}`).update(payload);
      Object.assign(city, payload);
      city._editing = false;
      city._draft = undefined;
      this.snack.open(`${city.name} updated`, 'OK', { duration: 2500 });
    } catch (e) {
      this.snack.open('Save failed. Please try again.', 'OK', { duration: 3000 });
    }
    city._saving = false;
  }

  // ── Add city ──────────────────────────────────────────────────────────────
  openAddPanel() {
    this.newCity = this.emptyCity();
    this.showAddPanel = true;
  }

  cancelAdd() {
    this.showAddPanel = false;
  }

  async addCity() {
    if (!this.newCity.name || !this.newCity.value) {
      this.snack.open('Name and Value are required.', 'OK', { duration: 2500 });
      return;
    }
    this.adding = true;
    const key = String(this.newCity.cityId || Date.now());
    const payload = {
      name: this.newCity.name,
      value: (this.newCity.value || '').toLowerCase().replace(/\s+/g, ''),
      cityId: Number(this.newCity.cityId) || 0,
      available: this.newCity.available === true,
      availableForCustomer: this.newCity.availableForCustomer === true,
      radiusForSearch: Number(this.newCity.radiusForSearch) || 10,
      gofloatersSpaceNameCounter: Number(this.newCity.gofloatersSpaceNameCounter) || 0,
      coords: {
        lat: Number(this.newCity.coords ? this.newCity.coords.lat : 0) || 0,
        long: Number(this.newCity.coords ? this.newCity.coords.long : 0) || 0
      },
    };
    try {
      await this.db.database.ref(`${DB_PATH}/${key}`).set(payload);
      const city = this.normalize(key, payload);
      this.cities.push(city);
      this.cities.sort((a, b) => a.name.localeCompare(b.name));
      this.applyFilter();
      this.showAddPanel = false;
      this.snack.open(`${city.name} added`, 'OK', { duration: 2500 });
    } catch (e) {
      this.snack.open('Add failed. Please try again.', 'OK', { duration: 3000 });
    }
    this.adding = false;
  }

  private emptyCity(): Partial<City> {
    return {
      name: '', value: '', cityId: null,
      available: true, availableForCustomer: true,
      radiusForSearch: 10, gofloatersSpaceNameCounter: 0,
      coords: { lat: 0, long: 0 },
    };
  }
}
